import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useLogs } from '../hooks/useLogs';
import { useAuthContext } from '../contexts/AuthContext';
import { logAction, getCurrentAdmin } from '../services/logger';
import { LogRow } from '../components/LogRow';
import { StatusBadge } from '../components/StatusBadge';
import { Users, Trophy, CheckCircle, ThumbsUp, UserPlus, Shield, Trash2, Check } from 'lucide-react';
import toast from 'react-hot-toast';

interface Stats {
  bars: number;
  matches: number;
  confirmedToday: number;
  validationsToday: number;
}

interface DiffusionStat {
  name: string;
  value: number;
  color: string;
}

export function DashboardPage() {
  const { isSuperAdmin, admin } = useAuthContext();
  const [stats, setStats] = useState<Stats>({ bars: 0, matches: 0, confirmedToday: 0, validationsToday: 0 });
  const [pieData, setPieData] = useState<DiffusionStat[]>([]);
  const [tonightDiffusions, setTonightDiffusions] = useState<any[]>([]);
  const [adminList, setAdminList] = useState<any[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('admin');
  const [inviting, setInviting] = useState(false);
  const { logs } = useLogs({ dateRange: 'today' });

  useEffect(() => {
    loadStats();
    if (isSuperAdmin) loadAdmins();
  }, [isSuperAdmin]);

  const loadStats = async () => {
    const today = new Date().toISOString().split('T')[0];

    const [barsRes, matchesRes, diffRes, confRes, tonightRes, validRes] = await Promise.all([
      supabase.from('bars').select('id', { count: 'exact', head: true }),
      supabase.from('matches').select('id', { count: 'exact', head: true }),
      supabase.from('diffusions').select('status'),
      supabase.from('diffusions').select('id', { count: 'exact', head: true }).eq('status', 'confirmed'),
      supabase.from('matches')
        .select('id, home_team, away_team, match_time, competition')
        .eq('match_date', today)
        .order('match_time'),
      supabase.from('bar_validations').select('id', { count: 'exact', head: true }).gte('created_at', today + 'T00:00:00'),
    ]);

    setStats({
      bars: barsRes.count || 0,
      matches: matchesRes.count || 0,
      confirmedToday: confRes.count || 0,
      validationsToday: validRes.count || 0,
    });

    const allDiffs = diffRes.data || [];
    setPieData([
      { name: 'Non vérifié', value: allDiffs.filter(d => d.status === 'unconfirmed').length, color: '#FF3B30' },
      { name: 'Probable', value: allDiffs.filter(d => d.status === 'probable').length, color: '#FF9F0A' },
      { name: 'Confirmé', value: allDiffs.filter(d => d.status === 'confirmed').length, color: '#30D158' },
    ]);

    if (tonightRes.data?.length) {
      const diffs = await supabase.from('diffusions')
        .select('id, match_id, status, bars(name, probability)')
        .in('match_id', tonightRes.data.map(m => m.id));

      const matchesWithDiffs = tonightRes.data.map(m => ({
        ...m,
        diffusions: (diffs.data || []).filter(d => d.match_id === m.id),
      }));
      setTonightDiffusions(matchesWithDiffs);
    }
  };

  const confirmHighProb = async (matchId: string) => {
    const match = tonightDiffusions.find((m: any) => m.id === matchId);
    if (!match) return;
    const highProb = match.diffusions.filter((d: any) => (d.bars?.probability ?? 0) >= 80 && d.status !== 'confirmed');
    if (highProb.length === 0) { toast('Aucune diffusion à confirmer'); return; }

    const adminData = getCurrentAdmin();
    for (const d of highProb) {
      await supabase.from('diffusions').update({ status: 'confirmed', confirmed_at: new Date().toISOString(), confirmed_by: 'admin' }).eq('id', d.id);
    }
    await logAction('confirm_diffusion', 'diffusion', null, null,
      { count: highProb.length, match: `${match.home_team} vs ${match.away_team}` },
      `${adminData?.name} a confirmé ${highProb.length} diffusions (prob ≥80%) pour ${match.home_team} vs ${match.away_team}`);
    toast.success(`${highProb.length} diffusions confirmées`);
    loadStats();
  };

  const loadAdmins = async () => {
    const { data } = await supabase.from('admin_users').select('*').order('created_at');
    setAdminList(data || []);
  };

  const handleInviteAdmin = async () => {
    if (!inviteEmail || !inviteName) { toast.error('Email et nom requis'); return; }
    setInviting(true);
    try {
      const { error } = await supabase.from('admin_users').insert({
        email: inviteEmail.trim().toLowerCase(), name: inviteName.trim(), role: inviteRole,
      });
      if (error) throw error;
      await logAction('invite_admin', 'admin', null, null,
        { email: inviteEmail, name: inviteName, role: inviteRole },
        `${admin?.name} a invité ${inviteName} comme ${inviteRole}`);
      toast.success(`${inviteName} ajouté comme ${inviteRole}`);
      setInviteEmail(''); setInviteName('');
      loadAdmins();
    } catch (e: any) { toast.error('Erreur: ' + e.message); }
    finally { setInviting(false); }
  };

  const handleRemoveAdmin = async (adminUser: any) => {
    if (adminUser.email === admin?.email) { toast.error('Tu ne peux pas te supprimer toi-même'); return; }
    if (!window.confirm(`Retirer ${adminUser.name} ?`)) return;
    await supabase.from('admin_users').delete().eq('id', adminUser.id);
    await logAction('remove_admin', 'admin', adminUser.id, { email: adminUser.email }, null,
      `${admin?.name} a retiré ${adminUser.name} des administrateurs`);
    toast.success(`${adminUser.name} retiré`);
    loadAdmins();
  };

  const STAT_CARDS = [
    { label: 'Bars', value: stats.bars, sub: 'en base', icon: Users, color: 'var(--accent)' },
    { label: 'Matchs', value: stats.matches, sub: 'à venir', icon: Trophy, color: 'var(--accent)' },
    { label: 'Confirmés', value: stats.confirmedToday, sub: "aujourd'hui", icon: CheckCircle, color: 'var(--green)' },
    { label: 'Validations', value: stats.validationsToday, sub: "ONpitchers", icon: ThumbsUp, color: 'var(--orange)' },
  ];

  return (
    <div>
      <div className="stats-grid">
        {STAT_CARDS.map(card => (
          <div key={card.label} className="stat-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div className="stat-label">{card.label}</div>
                <div className="stat-value" style={{ color: card.color }}>{card.value}</div>
                <div className="stat-sub">{card.sub}</div>
              </div>
              <card.icon size={20} style={{ color: 'var(--text-dim)' }} />
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div className="card">
          <h3 className="card-title">Diffusions par statut</h3>
          {pieData.some(d => d.value > 0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }} itemStyle={{ color: '#fff' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="muted">Aucune donnée</p>}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '8px' }}>
            {pieData.map(d => (
              <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-muted)' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: d.color, display: 'inline-block' }} />
                {d.name} ({d.value})
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h3 className="card-title">Matchs ce soir</h3>
          {tonightDiffusions.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {tonightDiffusions.map((m: any) => {
                const confirmed = m.diffusions.filter((d: any) => d.status === 'confirmed').length;
                const total = m.diffusions.length;
                const highProb = m.diffusions.filter((d: any) => (d.bars?.probability ?? 0) >= 80 && d.status !== 'confirmed').length;
                return (
                  <div key={m.id} className="tonight-row">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong style={{ fontSize: '13px' }}>{m.home_team} vs {m.away_team}</strong>
                        <span style={{ fontSize: '11px', color: 'var(--text-dim)', marginLeft: '8px' }}>{m.match_time}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '11px', color: confirmed === total ? 'var(--green)' : 'var(--text-muted)' }}>
                          {confirmed}/{total} bars
                        </span>
                        {highProb > 0 && (
                          <button className="btn btn-green" style={{ fontSize: '10px', padding: '3px 8px' }}
                            onClick={() => confirmHighProb(m.id)}>
                            <Check size={12} /> Confirmer {highProb}
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                      {m.diffusions.map((d: any, i: number) => (
                        <span key={i} style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                          <StatusBadge status={d.status} size="sm" />
                          <span style={{ marginLeft: '4px' }}>{d.bars?.name}</span>
                        </span>
                      ))}
                      {m.diffusions.length === 0 && <span className="muted" style={{ fontSize: '10px' }}>Aucune diffusion</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="muted">Aucun match ce soir</p>}
        </div>
      </div>

      <div className="card" style={{ marginTop: '20px' }}>
        <h3 className="card-title">Dernières actions</h3>
        {logs.slice(0, 5).map(log => <LogRow key={log.id} log={log} />)}
        {logs.length === 0 && <p className="muted">Aucune action</p>}
      </div>

      {isSuperAdmin && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Shield size={16} style={{ color: 'var(--accent)' }} /> Gestion des administrateurs
          </h3>
          <table className="data-table" style={{ marginBottom: '20px' }}>
            <thead><tr><th>Nom</th><th>Email</th><th>Rôle</th><th style={{ textAlign: 'right' }}>Action</th></tr></thead>
            <tbody>
              {adminList.map((a: any) => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 600 }}>{a.name}</td>
                  <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{a.email}</td>
                  <td>
                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600,
                      background: a.role === 'super_admin' ? 'var(--accent-dim)' : 'var(--blue-dim)',
                      color: a.role === 'super_admin' ? 'var(--accent)' : 'var(--blue)' }}>
                      {a.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {a.email !== admin?.email && (
                      <button className="btn-icon danger" onClick={() => handleRemoveAdmin(a)}><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '16px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <UserPlus size={16} style={{ color: 'var(--accent)' }} />
              <span style={{ fontSize: '13px', fontWeight: 600 }}>Inviter un administrateur</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px auto', gap: '10px', alignItems: 'end' }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label>Email</label>
                <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="hugo@email.com" />
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label>Nom</label>
                <input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Hugo Dupont" />
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label>Rôle</label>
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              <button className="btn btn-primary" onClick={handleInviteAdmin} disabled={inviting} style={{ height: '36px' }}>
                {inviting ? '...' : 'Inviter'}
              </button>
            </div>
            <p style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '8px' }}>
              L'admin devra créer son compte dans Supabase Auth avec cet email.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
